/**
 * Book Detail — the header: who, which tier, what state, and the three
 * operator actions.
 *
 * The action buttons only OPEN dialogs. Every network call lives in
 * BookDetailPage, behind a dialog confirm.
 */

import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  Button,
  Divider,
  Tooltip,
  Link,
} from '@mui/material';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import EventIcon from '@mui/icons-material/Event';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import { WeddingStatusChip, WeddingTierChip } from '../WeddingChips';
import { STATUS_META, centsToUsd, dateTimeInZone, deadlineCountdown, shortDate } from '../weddingFormat';
import type { BookDetail } from '../weddingApi';

interface Props {
  detail: BookDetail;
  onSuspend: () => void;
  onReactivate: () => void;
  onExtendDeadline: () => void;
}

const Fact: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 600 }}>
      {children}
    </Typography>
  </Box>
);

const BookStatsHeader: React.FC<Props> = ({ detail, onSuspend, onReactivate, onExtendDeadline }) => {
  const navigate = useNavigate();
  const { book } = detail;
  const suspended = book.status === 'suspended';
  const countdown = deadlineCountdown(book.contribution_deadline, book.couple_timezone);

  return (
    <Card sx={{ borderRadius: '16px', mb: 3 }}>
      <CardContent>
        <Button
          size="small"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/dashboard/wedding/books')}
          sx={{ mb: 1 }}
        >
          All books
        </Button>

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              {book.partner1_name} &amp; {book.partner2_name}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                {book.wedding_code}
              </Typography>
              <WeddingTierChip tier={book.tier} />
              <WeddingStatusChip status={book.status} />
              <Link href={`mailto:${book.couple_email}`} variant="body2" underline="hover">
                {book.couple_email}
              </Link>
            </Stack>
          </Box>

          <Stack direction="row" spacing={1}>
            <Button startIcon={<EventIcon />} variant="outlined" onClick={onExtendDeadline}>
              Move deadline
            </Button>
            {suspended ? (
              <Button startIcon={<PlayCircleIcon />} variant="contained" onClick={onReactivate}>
                Reactivate
              </Button>
            ) : (
              <Button
                startIcon={<PauseCircleIcon />}
                variant="outlined"
                color="error"
                onClick={onSuspend}
              >
                Suspend
              </Button>
            )}
          </Stack>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
          {STATUS_META[book.status]?.description}
        </Typography>

        <Divider sx={{ my: 2 }} />

        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)', lg: 'repeat(7, 1fr)' },
          }}
        >
          <Fact label="Wedding date">{shortDate(book.wedding_date)}</Fact>
          <Fact label="Deadline">
            <Tooltip title={dateTimeInZone(book.contribution_deadline, book.couple_timezone)}>
              <Typography
                component="span"
                variant="body2"
                sx={{ fontWeight: 600, color: countdown.overdue ? 'error.main' : 'inherit' }}
              >
                {countdown.label}
              </Typography>
            </Tooltip>
          </Fact>
          <Fact label="Timezone">{book.couple_timezone}</Fact>
          <Fact label="Contributor cap">
            {book.max_contributors}
            {book.extra_contributor_count > 0 ? ` (+${book.extra_contributor_count} bought)` : ''}
          </Fact>
          <Fact label="Revenue">{centsToUsd(detail.revenue_cents)}</Fact>
          <Fact label="Couple since">
            {shortDate(book.couple_created_at)}
            {book.couple_email_verified ? '' : ' · unverified'}
          </Fact>
          {/*
            WB-VOICE (session O). THE ONLY NUMBER IN THIS HEADER THAT IS ABOUT
            A THIRD PARTY'S CAPACITY RATHER THAN THIS COUPLE.

            Resemble's rapid-clone quota is 500 account-wide and members draw on
            the same pool, so a 300-guest Platinum wedding is most of it. This
            count is one of the three things standing between that arithmetic
            and a support incident (the others being the per-creation log line
            and the WeddingVoiceProfilesCreated alarm at 350).

            `?? 0` rather than a conditional render: a book with no voice
            profiles should read "0", and a dashboard bundle running against a
            backend older than migration 448 gets `voice_stats` undefined and
            should still render the grid rather than a gap.
          */}
          <Fact label="Voice profiles">
            {Number(detail.voice_stats?.voice_profiles ?? 0)}
            {Number(detail.voice_stats?.voice_profiles_pending ?? 0) > 0
              ? ` (${Number(detail.voice_stats?.voice_profiles_pending)} building)`
              : ''}
          </Fact>
        </Box>
      </CardContent>
    </Card>
  );
};

export default BookStatsHeader;
